import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import axiosInstance from '../../services/api/axiosInstance';

const WebsiteSelector = ({ 
  selectedSiteId, 
  onSiteChange, 
  teamId, 
  className = "",
  placeholder = "Select website",
  showLabel = true,
  labelText = "Website",
  onDomainChange = () => {}
}) => {
  const [websites, setWebsites] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState(null);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  // Fetch websites for the team
  useEffect(() => {
    const fetchWebsites = async () => {
      if (!teamId) return;
      
      // Check sessionStorage cache first
      const cacheKey = `websites_${teamId}`;
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        try {
          const { data, timestamp } = JSON.parse(cached);
          // Use cache if less than 5 minutes old
          if (Date.now() - timestamp < 5 * 60 * 1000) {
            console.log('Using cached websites data for team:', teamId);
            setWebsites(data);
            
            // Auto-select first website if none selected
            if (!selectedSiteId && data.length > 0) {
              onSiteChange(data[0].siteId);
              const firstDomain = data[0].Domain || data[0].domain || '';
              if (firstDomain) {
                onDomainChange(firstDomain.startsWith('http') ? firstDomain : `https://${firstDomain}`);
              }
            }
            return;
          }
        } catch (e) {
          console.warn('Failed to parse cached websites:', e);
        }
      }
      
      try {
        setIsLoading(true);
        setError(null);
        
        let response;
        const isObjectId = typeof teamId === 'string' && /^[a-fA-F0-9]{24}$/.test(teamId);
        if (isObjectId) {
          try {
            response = await axiosInstance.get(`/website/team/${teamId}`);
          } catch (err) {
            const teamName = localStorage.getItem('selectedTeam');
            response = await axiosInstance.get(`/website/name/${teamName}`);
          }
        } else {
          const teamName = localStorage.getItem('selectedTeam') || String(teamId);
          response = await axiosInstance.get(`/website/name/${teamName}`);
        }
        
        if (response.status === 200 && response.data.websites) {
          setWebsites(response.data.websites);
          
          // Cache the result in sessionStorage
          sessionStorage.setItem(cacheKey, JSON.stringify({
            data: response.data.websites,
            timestamp: Date.now()
          }));
          
          // If no site is selected and we have websites, select the first one
          if (!selectedSiteId && response.data.websites.length > 0) {
            const firstWebsite = response.data.websites[0];
            onSiteChange(firstWebsite.site_id || firstWebsite.siteId);
            const firstDomain = firstWebsite.domain || firstWebsite.Domain || '';
            if (firstDomain) {
              onDomainChange(firstDomain.startsWith('http') ? firstDomain : `https://${firstDomain}`);
            }
          }
        }
      } catch (err) {
        console.error('Error fetching websites:', err);
        setError('Failed to load websites');
      } finally {
        setIsLoading(false);
      }
    };

    fetchWebsites();
  }, [teamId]); // Removed selectedSiteId and onSiteChange to prevent re-fetching

  const selectedWebsite = websites.find(w => w.siteId === selectedSiteId);

  const handleSiteSelect = (siteId) => {
    // Keep localStorage in sync for components that restore by Domain or siteId
    try {
      const site = websites.find(w => w.siteId === siteId);
      if (site) {
        const normalizedDomain = site.Domain || site.domain || '';
        localStorage.setItem('selectedWebsite', normalizedDomain);
        onDomainChange(normalizedDomain.startsWith('http') ? normalizedDomain : `https://${normalizedDomain}`);
      }
      localStorage.setItem('idy', siteId);
    } catch (_) {}
    onSiteChange(siteId);
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  // Calculate dropdown position when opening
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  }, [isOpen]);

  return (
    <div className={`relative ${className}`}>
      {showLabel && (
        <label className="block text-xs font-medium mb-2 text-[#475569]">
          {labelText}
        </label>
      )}
      
      <div className="relative">
        <button
          ref={buttonRef}
          type="button"
          onClick={toggleDropdown}
          className="w-full px-4 py-3 text-sm rounded-r-none focus:outline-none border-r-0 border border-gray-300 bg-white hover:bg-gray-50 transition-all duration-200 flex items-center justify-between"
          style={{ fontFamily: 'Raleway, sans-serif' }}
          disabled={isLoading}
        >
          <div className="flex items-center gap-2">
            {isLoading ? (
              <>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse"></div>
                <span className="text-[#475569]">Loading...</span>
              </>
            ) : selectedWebsite ? (
              <span className="text-[#0f172a] truncate">
                {selectedWebsite.domain || selectedWebsite.Domain}
              </span>
            ) : (
              <span className="text-[#475569]">{placeholder}</span>
            )}
          </div>
          
          <svg 
            className={`w-4 h-4 text-[#475569] transition-transform duration-200 ml-auto ${isOpen ? 'rotate-180' : ''}`} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Dropdown - Rendered via Portal */}
        {isOpen && typeof document !== 'undefined' && createPortal(
          <div
            ref={dropdownRef}
            className="fixed bg-white border border-gray-300 rounded-lg shadow-xl max-h-60 overflow-auto"
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
              zIndex: 10000,
              fontFamily: 'Raleway, sans-serif'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {error ? (
              <div className="px-4 py-2 text-sm text-red-600">
                {error}
              </div>
            ) : websites.length === 0 ? (
              <div className="px-4 py-2 text-sm text-[#475569]">
                No websites found
              </div>
            ) : (
              websites.map((website) => (
                <button
                  key={website.site_id || website.siteId}
                  onClick={() => handleSiteSelect(website.site_id || website.siteId)}
                  className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors ${
                    selectedSiteId === (website.site_id || website.siteId) ? 'bg-[#4b57aa]/10 text-[#4b57aa]' : 'text-[#0f172a]'
                  }`}
                >
                  <span className="truncate">{website.domain || website.Domain}</span>
                  {(website.name || website.Name) && (
                    <span className="text-xs text-[#475569] truncate ml-1">({website.name || website.Name})</span>
                  )}
                </button>
              ))
            )}
          </div>,
          document.body
        )}
      </div>

      {/* Click outside to close */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-[9998]" 
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default WebsiteSelector;
